import { useEffect } from "react";
import FranklinTNHeroSection from "./components/HeroSection";
import FranklinTNStickyNav from "./components/StickyNav";
import FranklinTNProcessingTimeSection from "./components/ProcessingTimeSection";
import FranklinTNServicesSection from "./components/ServicesSection";
import FranklinTNLocalAreaSection from "./components/LocalAreaSection";
import FranklinTNTravelTimingSection from "./components/TravelTimingSection";
import FranklinTNDestinationsSection from "./components/DestinationsSection";
import FranklinTNTestimonialsSection from "./components/TestimonialsSection";
import FranklinTNAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import FranklinTNComparisonSection from "./components/ComparisonSection";
import FranklinTNMistakesSection from "./components/MistakesSection";
import FranklinTNHowItWorksSection from "./components/HowItWorksSection";
import FranklinTNFAQSection, { faqs } from "./components/FAQSection";
import FranklinTNHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import FranklinTNFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function FranklinTNPage() {
  useEffect(() => {
    document.title = "Emergency & Expedited Passport Services Franklin, TN — Williamson County | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/franklin-tn/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Franklin, TN and Williamson County — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Cool Springs executives, Brentwood families, Spring Hill residents, and Williamson County travelers. No Nashville agency lines required.");
    setMeta("geo.placename", "Franklin, Tennessee");
    setMeta("geo.region", "US-TN");
    setMeta("geo.position", "35.9251;-86.8689");
    setMeta("ICBM", "35.9251, -86.8689");

    const servicesList = [
      { name: "Emergency Passport Franklin TN", description: "Urgent passport processing for Franklin and Williamson County travelers with immediate international departure needs, as fast as 24 hours." },
      { name: "Expedited Passport Renewal Franklin TN", description: "Fast passport renewal for Cool Springs executives, Brentwood families, and Williamson County residents — 2-9 business days." },
      { name: "New Passport Franklin TN", description: "First-time passport application assistance for Franklin-area residents with acceptance facility scheduling support." },
      { name: "Child Passport Franklin TN", description: "Minor passport services for Williamson County families, with parental consent guidance and step-by-step acceptance clerk support." },
      { name: "Lost Stolen Passport Replacement Franklin TN", description: "Fast replacement of lost or stolen passports for Franklin and Williamson County residents." },
      { name: "Corporate Passport Services Franklin TN", description: "Expedited passport processing for Cool Springs corporate executives and business travelers with compressed international travel timelines." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Tennessee", item: "https://fastpassportcenter.com/tennessee/" },
          { "@type": "ListItem", position: 3, name: "Franklin Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Franklin, TN and Williamson County. Serving Cool Springs executives, Brentwood families, Spring Hill residents, and all of Williamson County.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Franklin",
          containedInPlace: {
            "@type": "State",
            name: "Tennessee",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Franklin",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data-franklin-tn";
    document.getElementById("structured-data-franklin-tn")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data-franklin-tn");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <FranklinTNHeroSection />
      <FranklinTNStickyNav />
      <FranklinTNProcessingTimeSection />
      <FranklinTNServicesSection />
      <FranklinTNLocalAreaSection />
      <FranklinTNTravelTimingSection />
      <FranklinTNDestinationsSection />
      <FranklinTNTestimonialsSection />
      <FranklinTNAcceptanceFacilitiesSection />
      <FranklinTNComparisonSection />
      <FranklinTNMistakesSection />
      <FranklinTNHowItWorksSection />
      <FranklinTNFAQSection />
      <FranklinTNHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/franklin-tn" state="TN" />
      <FranklinTNFinalCTA />
    </div>
  );
}